// Shared pieces of the Plan & Procurement list page: query building for
// GET /procurement, row status rules, and the Excel/CSV export.

import * as XLSX from 'xlsx';
import { api } from '../../../config';
import {
  formatDateToDDMMYYYY,
  formatDateToddmmyyhhmm,
} from '../../../utils/dateformatter';
import { getListProductTypeLabel } from '../../../shared/offer-domain/constants';

const DAY_MS = 24 * 60 * 60 * 1000;

export const PROCUREMENT_TYPE_HEADERS = {
  'product-hydro': '1',
  'hydrocarbon-condensat': '2',
};

export const PROCUREMENT_TYPE_MAP = {
  PelelanganKhusus: 'Pelelangan Khusus',
  PemilihanLangsung: 'Pemilihan Langsung',
  TunjukLangsung: 'Tunjuk Langsung',
};

export const CONTRACT_TYPE_MAP = {
  1: { label: 'Term', tone: 'term' },
  2: { label: 'Spot', tone: 'spot' },
};

export const getContractType = contractType =>
  CONTRACT_TYPE_MAP[Number(contractType)] || null;

// First keyword match wins; anything else falls back to a box icon.
const PRODUCT_ICONS = [
  { match: ['propane', 'butane'], icon: 'ri-fire-line' },
  { match: ['lpg', 'gas'], icon: 'ri-gas-station-line' },
  { match: ['condensat', 'crude', 'oil'], icon: 'ri-oil-line' },
  { match: ['hydro', 'naphtha', 'solvent'], icon: 'ri-flask-line' },
];

const PRODUCT_TONES = ['blue', 'teal', 'amber', 'violet', 'green', 'rose'];

// Same product name always gets the same icon and color.
export const getProductVisual = name => {
  const key = String(name || '').toLowerCase();
  const icon =
    PRODUCT_ICONS.find(entry => entry.match.some(word => key.includes(word)))
      ?.icon || 'ri-box-3-line';
  const hash = [...key].reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return { icon, tone: PRODUCT_TONES[hash % PRODUCT_TONES.length] };
};

export const STEP_DEFS = [
  {
    key: 'isProcurementRequirementCompleted',
    label: 'Procurement Requirement',
    icon: 'ri-file-list-3-line',
  },
  {
    key: 'isSupportingDocumentsCompleted',
    label: 'Supporting Document',
    icon: 'ri-folder-2-line',
  },
  {
    key: 'isDMUTInvitationCompleted',
    label: 'Registered Supplier Invitation',
    icon: 'ri-team-line',
  },
];

// Draft-only insight filters, backed by IsReadyToSubmit / DeadlineStatus.
export const QUICK_FILTERS = [
  {
    key: 'ready',
    label: 'ready to submit',
    icon: 'ri-rocket-2-line',
    tone: 'success',
    params: { IsReadyToSubmit: 'true' },
  },
  {
    key: 'due-soon',
    label: 'due in 7 days',
    icon: 'ri-timer-flash-line',
    tone: 'warning',
    params: { DeadlineStatus: 'due-soon' },
  },
  {
    key: 'overdue',
    label: 'overdue',
    icon: 'ri-alarm-warning-line',
    tone: 'danger',
    params: { DeadlineStatus: 'overdue' },
  },
];

export const getQuickFilter = key => QUICK_FILTERS.find(f => f.key === key);

export const buildListUrl = ({
  currentPage,
  rowsPerPage,
  procurementTypeHeader,
  search,
  completedFilter,
  quickFilter,
  contractType,
  procurementType,
  sortField,
  sortDirection,
}) => {
  const params = new URLSearchParams();
  params.set('PageNumber', String(currentPage));
  params.set('PageSize', String(rowsPerPage));
  if (procurementTypeHeader) {
    params.set('ProcurementTypeHeader', procurementTypeHeader);
  }
  if (search?.trim()) params.set('Search', search.trim());
  if (completedFilter) params.set('IsProcurementCompleted', completedFilter);
  if (contractType) params.set('ContractType', contractType);
  if (procurementType) params.set('ProcurementType', procurementType);
  Object.entries(getQuickFilter(quickFilter)?.params || {}).forEach(
    ([key, value]) => params.set(key, value)
  );
  if (sortField) {
    params.set('SortBy', sortField);
    params.set('IsDescending', sortDirection === 'desc' ? 'true' : 'false');
  }
  return `${api.API_URL}/procurement?${params.toString()}`;
};

export const fetchJson = async (url, token, signal) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'same-origin',
    signal,
  });
  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
  return response.json();
};

export const getLastUpdate = row =>
  row?.updatedDate || row?.createdDate || null;

export const isReadyToSubmit = row =>
  !row?.isProcurementCompleted &&
  STEP_DEFS.every(step => row?.[step.key] === true);

export const getStatusLabel = row => {
  if (row?.isProcurementCompleted) return 'Submitted';
  return isReadyToSubmit(row) ? 'Ready' : 'Draft';
};

// Compares calendar days so "today" means the same date, not the next 24h.
export const getDeadlineInfo = (deadline, isSubmitted) => {
  const date = deadline ? new Date(deadline) : null;
  if (!date || Number.isNaN(date.getTime())) return null;

  const startOfDay = value =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate());
  const days = Math.round((startOfDay(date) - startOfDay(new Date())) / DAY_MS);
  const isPast = date.getTime() < Date.now();

  if (isSubmitted) return { tone: 'muted', label: 'Closed', days };
  if (isPast) return { tone: 'danger', label: 'Past Deadline', days };
  if (days === 0) return { tone: 'danger', label: 'Due today', days };
  if (days === 1) return { tone: 'danger', label: '1 day left', days };
  if (days < 7) return { tone: 'warning', label: `${days} days left`, days };
  return { tone: 'muted', label: `${days} days left`, days };
};

const toExportRow = row => {
  const deadline = row.proposalSubmissionDeadline;
  const stepState = key => (row[key] === true ? 'Done' : 'Open');

  return {
    'ID Proc': row.procurementNumber || '',
    Title: row.titleProcurement || '',
    Product: row.productType?.product?.name || '',
    'Product Type':
      getListProductTypeLabel(row, row.productType?.description) || '',
    'Proc Type': PROCUREMENT_TYPE_MAP[row.procurementType] || '',
    Contract: getContractType(row.contractType)?.label || '',
    Description: row.description || '',
    'Proposal Deadline': deadline ? formatDateToddmmyyhhmm(deadline) : '',
    'Deadline Status':
      getDeadlineInfo(deadline, row.isProcurementCompleted)?.label || '',
    'Procurement Requirement': stepState('isProcurementRequirementCompleted'),
    'Supporting Document': stepState('isSupportingDocumentsCompleted'),
    'Registered Supplier Invitation': stepState('isDMUTInvitationCompleted'),
    Status: getStatusLabel(row),
    'Created By': row.createdBy || '',
    'Created Date': row.createdDate
      ? formatDateToDDMMYYYY(row.createdDate)
      : '',
    'Updated By': row.updatedBy || '',
    'Last Update': getLastUpdate(row)
      ? formatDateToddmmyyhhmm(getLastUpdate(row))
      : '',
  };
};

const triggerDownload = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const todayStamp = () => {
  const today = new Date();
  return [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0'),
  ].join('');
};

// Writes plain row objects to a downloaded .xlsx or .csv file named
// `<prefix>_<sheetName>_<yyyymmdd>`. format: 'xlsx' | 'csv'
export const downloadSheet = (rows, { format, prefix, sheetName }) => {
  const sheet = XLSX.utils.json_to_sheet(rows);
  const baseName = `${prefix}_${sheetName.replace(/[^A-Za-z0-9]+/g, '_')}_${todayStamp()}`;

  if (format === 'csv') {
    // BOM so Excel opens the UTF-8 CSV with the right encoding.
    const csv = `\uFEFF${XLSX.utils.sheet_to_csv(sheet)}`;
    triggerDownload(
      new Blob([csv], { type: 'text/csv;charset=utf-8' }),
      `${baseName}.csv`
    );
    return;
  }

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName.slice(0, 31));
  const out = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  triggerDownload(
    new Blob([out], { type: 'application/octet-stream' }),
    `${baseName}.xlsx`
  );
};

export const exportProcurementList = (items, { format, sheetName }) =>
  downloadSheet(items.map(toExportRow), {
    format,
    prefix: 'Procurement',
    sheetName,
  });
